import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { Document, Page, pdfjs } from "react-pdf";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import Tesseract from "tesseract.js";
import "./ResumeOCR.css";
import { useNavigate } from "react-router-dom";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

const PDF_WIDTH = 595;
const PDF_HEIGHT = 842;

// ─────────────────────────────────────────────────────────────────────────────
// Helper: preprocess canvas for better OCR accuracy (grayscale + contrast boost)
// ─────────────────────────────────────────────────────────────────────────────
function preprocessCanvasForOCR(srcCanvas) {
  const out = document.createElement("canvas");
  out.width = srcCanvas.width * 2;
  out.height = srcCanvas.height * 2;

  const ctx = out.getContext("2d");
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(srcCanvas, 0, 0, out.width, out.height);

  const imageData = ctx.getImageData(0, 0, out.width, out.height);
  const data = imageData.data;

  for (let i = 0; i < data.length; i += 4) {
    const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    const contrasted = Math.min(255, Math.max(0, 1.8 * (gray - 128) + 128));
    const final =
      contrasted < 140 ? contrasted * 0.6 : Math.min(255, contrasted * 1.1);

    data[i] = final;
    data[i + 1] = final;
    data[i + 2] = final;
  }

  ctx.putImageData(imageData, 0, 0);
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper: crop a region from the react-pdf rendered <canvas> element
// ─────────────────────────────────────────────────────────────────────────────
function cropPdfCanvas(pdfCanvasEl, crop, displayWidth, displayHeight) {
  const scaleX = pdfCanvasEl.width / displayWidth;
  const scaleY = pdfCanvasEl.height / displayHeight;

  const out = document.createElement("canvas");
  out.width = Math.round(crop.width * scaleX);
  out.height = Math.round(crop.height * scaleY);

  out
    .getContext("2d")
    .drawImage(
      pdfCanvasEl,
      Math.round(crop.x * scaleX),
      Math.round(crop.y * scaleY),
      out.width,
      out.height,
      0,
      0,
      out.width,
      out.height,
    );
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
function ResumeOCR() {
  const selectedFieldRef = useRef(null);
  const navigate = useNavigate();

  // ── PDF viewer state ──────────────────────────────────────────────────────
  const pdfContainerRef = useRef(null);
  const pdfWrapperRef = useRef(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfScale, setPdfScale] = useState(1);

  // ── ✅ NEW: Rotation state ────────────────────────────────────────────────
  const [rotation, setRotation] = useState(0);

  // ── OCR / crop state ──────────────────────────────────────────────────────
  const [crop, setCrop] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [ocrProgress, setOcrProgress] = useState(0);
  const [ocrMode, setOcrMode] = useState("idle");
  const [pdfPageSize, setPdfPageSize] = useState({ width: 0, height: 0 });

  // ── Error popup state ─────────────────────────────────────────────────────
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmittingError, setIsSubmittingError] = useState(false);

  // ── Validation state ──────────────────────────────────────────────────────
  const [validationErrors, setValidationErrors] = useState({
    mobile: "",
    email: "",
    aadhar: "",
    pinCode: "",
  });

  // ── Post / payment options ────────────────────────────────────────────────
  const postOptions = [
    { hindi: "स्टेनो टाइपिस्ट", english: "Steno Typist" },
    { hindi: "आशुलिपिक", english: "Stenographer" },
    { hindi: "सूचीकार (केटलॉग)", english: "Cataloguer" },
    { hindi: "ओवरसियर", english: "Overseer" },
    { hindi: "प्रयोगशाला सहायक", english: "Laboratory Assistant" },
    { hindi: "लिपिक / कनिष्ठ सहायक", english: "Clerk / Junior Assistant" },
  ];

  const vibhagOptions = [
    { hindi: "मनोविज्ञान विभाग", english: "Psychology Department" },
    { hindi: "सैन्य अध्ययन विभाग", english: "Military Studies Department" },
    { hindi: "गणित विभाग", english: "Mathematics Department" },
    { hindi: "रसायन विज्ञान विभाग", english: "Chemistry Department" },
    { hindi: "भौतिक विज्ञान विभाग", english: "Physics Department" },
    { hindi: "जन्तु विज्ञान विभाग", english: "Zoology Department" },
    { hindi: "वनस्पति विज्ञान विभाग", english: "Botany Department" },
  ];

  const paymentOptions = ["Bank Draft", "NEFT", "Check", "UPI", "IMPS", "RTGS"];

  // ── Photo & Signature state ───────────────────────────────────────────────
  const [photoSignUploading, setPhotoSignUploading] = useState(false);
  const [cropPreview, setCropPreview] = useState(null);

  // ── Refs ──────────────────────────────────────────────────────────────────
  const foldersRef = useRef([]);
  const currentFolderIndexRef = useRef(0);
  const ocrLoadingRef = useRef(false);
  const ocrStatusRef = useRef("");
  const formRef = useRef({
    post: "",
    vibhag: "",
    nameEnglish: "",
    nameHindi: "",
    fatherName: "",
    dob: "",
    gender: "",
    category: "",
    address: "",
    mobile: "",
    email: "",
    religion: "",
    caste: "",
    hindiTyping: "",
    englishTyping: "",
    shorthandSpeed: "",
    aadharNumber: "",
    paymentMethod: "",
    paymentNumber: "",
    pinCode: "",
    correspondenceAddress: "",
    sameAddress: false,
  });
  const eduRowsRef = useRef([]);
  const workRowsRef = useRef([]);

  // ── UPDATED defaultEduRows ────────────────────────────────────────────────
  const defaultEduRows = [
    {
      id: 1,
      exam: "",
      customExam: "",
      board: "",
      isBoardChecked: false,
      totalMarks: "",
      obtainedMarks: "",
      percentage: "",
      scoreType: "Percentage",
    },
  ];

  const defaultWorkRows = [
    { id: 1, orgName: "", duration: "", post: "", salary: "" },
  ];

  // ── React state (UI) ──────────────────────────────────────────────────────
  const [selectedField, setSelectedField] = useState(null);
  const [folders, setFolders] = useState([]);
  const [currentFolderIndex, setCurrentFolderIndex] = useState(0);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrStatus, setOcrStatus] = useState("");
  const [form, setForm] = useState(formRef.current);
  const [eduRows, setEduRows] = useState(defaultEduRows);
  const [workRows, setWorkRows] = useState(defaultWorkRows);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState(null);

  // ── Resizable splitter state & refs ──────────────────────────────────────
  const [leftWidthPct, setLeftWidthPct] = useState(45);
  const isResizing = useRef(false);
  const mainContainerRef = useRef(null);
  const leftPanelRef = useRef(null);

  const onSplitterMouseDown = useCallback((e) => {
    e.preventDefault();
    isResizing.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const onMove = (mv) => {
      if (!isResizing.current || !mainContainerRef.current) return;
      const { left, width } = mainContainerRef.current.getBoundingClientRect();
      const newPct = ((mv.clientX - left) / width) * 100;
      const clamped = Math.min(Math.max(newPct, 20), 75);
      if (leftPanelRef.current) {
        leftPanelRef.current.style.width = `${clamped}%`;
      }
    };

    const onUp = (mv) => {
      isResizing.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      if (mainContainerRef.current) {
        const { left, width } =
          mainContainerRef.current.getBoundingClientRect();
        const finalPct = ((mv.clientX - left) / width) * 100;
        setLeftWidthPct(Math.min(Math.max(finalPct, 20), 75));
      }
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, []);

  // ── Validation helpers ────────────────────────────────────────────────────
  const validateMobile = (v) => {
    if (!v) return "";
    return /^\d{10}$/.test(v) ? "" : "कृपया 10 अंकों का मोबाइल नंबर दर्ज करें";
  };
  const validateEmail = (v) => {
    if (!v) return "";
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(v)
      ? ""
      : "कृपया वैध ईमेल आईडी दर्ज करें (जैसे: name@example.com)";
  };
  const validateAadhar = (v) => {
    if (!v) return "";
    return /^\d{12}$/.test(v) ? "" : "कृपया 12 अंकों का आधार नंबर दर्ज करें";
  };
  const validatePinCode = (v) => {
    if (!v) return "";
    return /^\d{6}$/.test(v) ? "" : "कृपया 6 अंकों का पिन कोड दर्ज करें";
  };

  const handleMobileChange = (v) => {
    const d = v.replace(/\D/g, "");
    setValidationErrors((p) => ({ ...p, mobile: validateMobile(d) }));
    const u = { ...formRef.current, mobile: d };
    setForm(u);
    formRef.current = u;
  };
  const handleEmailChange = (v) => {
    setValidationErrors((p) => ({ ...p, email: validateEmail(v) }));
    const u = { ...formRef.current, email: v };
    setForm(u);
    formRef.current = u;
  };
  const handleAadharChange = (v) => {
    const d = v.replace(/\D/g, "");
    setValidationErrors((p) => ({ ...p, aadhar: validateAadhar(d) }));
    const u = { ...formRef.current, aadharNumber: d };
    setForm(u);
    formRef.current = u;
  };
  const handlePinCodeChange = (v) => {
    const d = v.replace(/\D/g, "").slice(0, 6);
    setValidationErrors((p) => ({ ...p, pinCode: validatePinCode(d) }));
    const u = { ...formRef.current, pinCode: d };
    setForm(u);
    formRef.current = u;
  };

  // ── PDF zoom / fit helpers ────────────────────────────────────────────────
  const calcFit = () => {
    if (!pdfContainerRef.current) return;
    const w = pdfContainerRef.current.offsetWidth - 48;
    const h = pdfContainerRef.current.offsetHeight - 32;
    setPdfScale(Math.min(w / PDF_WIDTH, h / PDF_HEIGHT));
  };
  const fitWidth = () =>
    pdfContainerRef.current &&
    setPdfScale((pdfContainerRef.current.offsetWidth - 48) / PDF_WIDTH);
  const fitHeight = () =>
    pdfContainerRef.current &&
    setPdfScale((pdfContainerRef.current.offsetHeight - 32) / PDF_HEIGHT);
  const zoomIn = () => setPdfScale((s) => Math.min(+(s + 0.15).toFixed(2), 4));
  const zoomOut = () =>
    setPdfScale((s) => Math.max(+(s - 0.15).toFixed(2), 0.2));

  // ── ✅ NEW: Rotation handlers ─────────────────────────────────────────────
  // Rotate counter-clockwise by 90°, keeping value in [0, 270]
  const rotateLeft = () => setRotation((r) => (r - 90 + 360) % 360);
  // Rotate clockwise by 90°, keeping value in [0, 270]
  const rotateRight = () => setRotation((r) => (r + 90) % 360);

  function onPdfLoadSuccess({ numPages }) {
    setNumPages(numPages);
    setPageNumber(1);
  }

  function onPdfPageRenderSuccess() {
    if (!pdfWrapperRef.current) return;
    const canvas = pdfWrapperRef.current.querySelector("canvas");
    if (canvas) {
      setPdfPageSize({
        width: canvas.offsetWidth,
        height: canvas.offsetHeight,
      });
    }
  }

  useEffect(()=>{
        const login = sessionStorage.getItem("workerToken");
        if (!login){
          alert("Login First");
          navigate("/");
        }
      });

  // ── Sync refs ─────────────────────────────────────────────────────────────
  useEffect(() => {
    selectedFieldRef.current = selectedField;
    foldersRef.current = folders;
    currentFolderIndexRef.current = currentFolderIndex;
    ocrLoadingRef.current = ocrLoading;
    ocrStatusRef.current = ocrStatus;
    formRef.current = form;
    eduRowsRef.current = eduRows;
    workRowsRef.current = workRows;
  }, [
    selectedField,
    folders,
    currentFolderIndex,
    ocrLoading,
    ocrStatus,
    form,
    eduRows,
    workRows,
  ]);

  useEffect(() => {
    eduRowsRef.current = defaultEduRows;
    workRowsRef.current = defaultWorkRows;
    calcFit();
    window.addEventListener("resize", calcFit);
    return () => window.removeEventListener("resize", calcFit);
  }, []);

  // ── Fetch folders ─────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchFolders = async () => {
      try {
        const phoneNumber = sessionStorage.getItem("phoneNumber");
        if (!phoneNumber) {
          alert("Phone number not found. Please login again.");
          return;
        }
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/folders`,
          {
            params: { phonenumber: phoneNumber },
            
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("workerToken")}`
            }
          },
          
        );
        const data = Array.isArray(res.data) ? res.data : [];
        setFolders(data);
        foldersRef.current = data;
        setCurrentFolderIndex(0);
        currentFolderIndexRef.current = 0;
      } catch (e) {
        console.log(e);
      }
    };
    fetchFolders();
  }, []);

  // ── Load candidate when index changes ────────────────────────────────────
  useEffect(() => {
    const folder = folders[currentFolderIndex];
    if (folder) {
      setCurrentPdfUrl(folder.url);
      setNumPages(null);
      setPageNumber(1);
      setCrop(null);
      setCompletedCrop(null);
      setOcrMode("idle");
      setCropPreview(null);
      setPhotoSignUploading(false);
      // ✅ Reset rotation when candidate changes
      setRotation(0);
      setTimeout(() => loadCandidateData(folder.name), 0);
    }
  }, [currentFolderIndex, folders]);

  // ── Keyboard navigation ───────────────────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName)) return;
      if (e.key === "ArrowUp") prevCandidate();
      if (e.key === "ArrowDown") nextCandidate();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const loadCandidateData = (folderName) => {
    setPdfLoading(true);
    axios
      .get(`${process.env.REACT_APP_API_URL}/load/${folderName}`,{
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("workerToken")}`
            }})
      .then((res) => {
        if (res.data) {
          if (res.data.form) {
            setForm(res.data.form);
            formRef.current = res.data.form;
            setValidationErrors({
              mobile: validateMobile(res.data.form.mobile || ""),
              email: validateEmail(res.data.form.email || ""),
              aadhar: validateAadhar(res.data.form.aadharNumber || ""),
              pinCode: validatePinCode(res.data.form.pinCode || ""),
            });
          }
          if (res.data.eduRows) {
            setEduRows(res.data.eduRows);
            eduRowsRef.current = res.data.eduRows;
          }
          if (res.data.workRows) {
            setWorkRows(res.data.workRows);
            workRowsRef.current = res.data.workRows;
          }
        } else {
          resetFormState();
        }
      })
      .catch(() => {})
      .finally(() => setPdfLoading(false));
  };

  const resetFormState = () => {
    const empty = {
      post: "",
      vibhag: "",
      nameEnglish: "",
      nameHindi: "",
      fatherName: "",
      dob: "",
      gender: "",
      category: "",
      address: "",
      mobile: "",
      email: "",
      religion: "",
      caste: "",
      hindiTyping: "",
      englishTyping: "",
      shorthandSpeed: "",
      aadharNumber: "",
      paymentMethod: "",
      paymentNumber: "",
      pinCode: "",
      correspondenceAddress: "",
      sameAddress: false,
    };
    setForm(empty);
    formRef.current = empty;
    setEduRows(defaultEduRows);
    eduRowsRef.current = defaultEduRows;
    setWorkRows(defaultWorkRows);
    workRowsRef.current = defaultWorkRows;
    setValidationErrors({ mobile: "", email: "", aadhar: "", pinCode: "" });
    setCropPreview(null);
    setPhotoSignUploading(false);
  };

  const saveData = async () => {
    const mErr = validateMobile(formRef.current.mobile);
    const eErr = validateEmail(formRef.current.email);
    const aErr = validateAadhar(formRef.current.aadharNumber);
    const pErr = validatePinCode(formRef.current.pinCode);
    setValidationErrors({
      mobile: mErr,
      email: eErr,
      aadhar: aErr,
      pinCode: pErr,
    });
    if (mErr || eErr || aErr || pErr) {
      alert(
        "❌ कृपया वैध मोबाइल नंबर (10 अंक), ईमेल, आधार नंबर (12 अंक) और पिन कोड (6 अंक) दर्ज करें",
      );
      return;
    }
    const folder = foldersRef.current[currentFolderIndexRef.current]?.name;
    if (!folder) return;
    const isBoardChecked = eduRowsRef.current.map((r) => ({
      id: r.id,
      exam: r.exam,
      customExam: r.customExam || "",
      isChecked: r.isBoardChecked || false,
    }));
    const payload = {
      worker_mobile: sessionStorage.getItem("phoneNumber"),
      folder,
      form: formRef.current,
      eduRows: eduRowsRef.current,
      workRows: workRowsRef.current,
      isBoardChecked: JSON.stringify(isBoardChecked),
      phoneNumber: sessionStorage.getItem("phoneNumber"),
    };
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/save`, payload,{
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("workerToken")}`
            }});
      alert("✅ डेटा सुरक्षित हो गया!");
    } catch (e) {
      console.error(e);
      alert("❌ Save करने में त्रुटि हुई");
    }
  };

  const nextCandidate = () => {
    if (currentFolderIndexRef.current < foldersRef.current.length - 1) {
      const i = currentFolderIndexRef.current + 1;
      setCurrentFolderIndex(i);
      currentFolderIndexRef.current = i;
      setShowErrorPopup(false);
      setErrorMessage("");
      setValidationErrors({ mobile: "", email: "", aadhar: "", pinCode: "" });
      setCrop(null);
      setCompletedCrop(null);
      setOcrMode("idle");
      setCropPreview(null);
      setPhotoSignUploading(false);
      // ✅ Reset rotation on candidate change
      setRotation(0);
    }
  };
  const prevCandidate = () => {
    if (currentFolderIndexRef.current > 0) {
      const i = currentFolderIndexRef.current - 1;
      setCurrentFolderIndex(i);
      currentFolderIndexRef.current = i;
      setShowErrorPopup(false);
      setErrorMessage("");
      setValidationErrors({ mobile: "", email: "", aadhar: "", pinCode: "" });
      setCrop(null);
      setCompletedCrop(null);
      setOcrMode("idle");
      setCropPreview(null);
      setPhotoSignUploading(false);
      // ✅ Reset rotation on candidate change
      setRotation(0);
    }
  };

  // ── Error reporting ───────────────────────────────────────────────────────
  const reportError = async () => {
    const phoneNumber = sessionStorage.getItem("phoneNumber");
    const currentFolder = foldersRef.current[currentFolderIndexRef.current];
    if (!phoneNumber) {
      alert("Phone number not found.");
      return;
    }
    if (!currentFolder) {
      alert("No candidate selected.");
      return;
    }
    if (!errorMessage.trim()) {
      alert("कृपया त्रुटि विवरण दर्ज करें");
      return;
    }
    setIsSubmittingError(true);
    try {
      const payload = {
        phoneNumber,
        workerName: sessionStorage.getItem("workerName") || "Unknown",
        bundleName: currentFolder.name,
        errorMessage: errorMessage.trim(),
      };
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/report-error-bundle`,
        payload,
        {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("workerToken")}`
            }}
      );
      if (res.data.success) {
        alert("✅ त्रुटि सफलतापूर्वक रिपोर्ट की गई!");
        setShowErrorPopup(false);
        setErrorMessage("");
        const updated = foldersRef.current.filter(
          (_, i) => i !== currentFolderIndexRef.current,
        );
        setFolders(updated);
        foldersRef.current = updated;
        if (updated.length === 0) {
          setCurrentFolderIndex(0);
          currentFolderIndexRef.current = 0;
          setCurrentPdfUrl(null);
          resetFormState();
        } else if (currentFolderIndexRef.current >= updated.length) {
          const ni = updated.length - 1;
          setCurrentFolderIndex(ni);
          currentFolderIndexRef.current = ni;
        }
      } else {
        alert(
          `❌ त्रुटि रिपोर्ट करने में समस्या: ${res.data.message || "Unknown error"}`,
        );
      }
    } catch (e) {
      console.error(e);
      alert("❌ त्रुटि रिपोर्ट करने में समस्या हुई।");
    } finally {
      setIsSubmittingError(false);
    }
  };

  // ── Education / Work row helpers ──────────────────────────────────────────
  const calculatePercentage = (obtained, total) => {
    const o = parseFloat(obtained);
    const t = parseFloat(total);
    if (!Number.isFinite(o) || !Number.isFinite(t) || t <= 0) return "";
    return ((o / t) * 100).toFixed(2);
  };

  const updateEduRow = (id, field, value) => {
    const rows = eduRowsRef.current.map((row) => {
      if (row.id !== id) return row;
      let updatedRow = { ...row, [field]: value };

      if (field === "scoreType") {
        updatedRow.percentage = "";
      }

      if (field === "totalMarks" || field === "obtainedMarks") {
        const scoreType = updatedRow.scoreType || "Percentage";
        if (scoreType === "Percentage") {
          const total = field === "totalMarks" ? value : updatedRow.totalMarks;
          const obtained =
            field === "obtainedMarks" ? value : updatedRow.obtainedMarks;
          updatedRow.percentage = calculatePercentage(obtained, total);
        } else if (scoreType === "CGPA" || scoreType === "Grade") {
          const obtainedValue =
            field === "obtainedMarks" ? value : updatedRow.obtainedMarks;
          updatedRow.percentage = obtainedValue || "";
        }
      }

      return updatedRow;
    });
    setEduRows(rows);
    eduRowsRef.current = rows;
  };

  const addEduRow = () => {
    const newRow = {
      id: Date.now(),
      exam: "",
      customExam: "",
      board: "",
      isBoardChecked: false,
      totalMarks: "",
      obtainedMarks: "",
      percentage: "",
      scoreType: "Percentage",
    };
    const rows = [...eduRowsRef.current, newRow];
    setEduRows(rows);
    eduRowsRef.current = rows;
  };

  const deleteEduRow = (id) => {
    const rows = eduRowsRef.current.filter((r) => r.id !== id);
    setEduRows(rows);
    eduRowsRef.current = rows;
  };

  const updateWorkRow = (id, field, value) => {
    const rows = workRowsRef.current.map((r) =>
      r.id === id ? { ...r, [field]: value } : r,
    );
    setWorkRows(rows);
    workRowsRef.current = rows;
  };

  const addWorkRow = () => {
    const rows = [
      ...workRowsRef.current,
      {
        id: workRowsRef.current.length + 1,
        orgName: "",
        duration: "",
        post: "",
        salary: "",
      },
    ];
    setWorkRows(rows);
    workRowsRef.current = rows;
  };

  // ── Field CSS helper ──────────────────────────────────────────────────────
  const fieldClass = (name) => {
    let c = "field-input";
    if (selectedFieldRef.current === name) c += " active";
    if (ocrLoadingRef.current && selectedFieldRef.current === name)
      c += " loading-field";
    return c;
  };

  const getPaymentNumberPlaceholder = () => {
    switch (formRef.current.paymentMethod) {
      case "Check":
        return "चेक नंबर दर्ज करें";
      case "UPI":
        return "UPI आईडी दर्ज करें (जैसे: name@bank)";
      case "Bank":
        return "बैंक खाता संख्या दर्ज करें";
      case "NEFT":
        return "NEFT लेन-देन संख्या दर्ज करें";
      case "IMPS":
        return "IMPS लेन-देन संख्या दर्ज करें";
      case "RTGS":
        return "RTGS लेन-देन संख्या दर्ज करें";
      default:
        return "भुगतान संख्या दर्ज करें";
    }
  };

  // ── OCR ───────────────────────────────────────────────────────────────────
  const runOCR = useCallback(async () => {
    if (!selectedFieldRef.current) {
      alert("पहले कोई field पर click करें");
      return;
    }
    if (!completedCrop || !completedCrop.width || !completedCrop.height) {
      alert("PDF पर कोई क्षेत्र चुनें (खींचकर select करें)");
      return;
    }

    const pdfCanvas = pdfWrapperRef.current?.querySelector("canvas");
    if (!pdfCanvas) {
      alert("PDF canvas नहीं मिला। PDF लोड होने तक प्रतीक्षा करें।");
      return;
    }

    const cropped = cropPdfCanvas(
      pdfCanvas,
      completedCrop,
      pdfPageSize.width,
      pdfPageSize.height,
    );

    const processedCanvas = preprocessCanvasForOCR(cropped);

    setOcrLoading(true);
    ocrLoadingRef.current = true;
    setOcrStatus("loading");
    ocrStatusRef.current = "loading";
    setOcrMode("processing");
    setOcrProgress(0);

    try {
      const result = await Tesseract.recognize(processedCanvas, "eng+hin", {
        logger: (m) => {
          if (m.status === "recognizing text") {
            setOcrProgress(Math.round(m.progress * 100));
          }
        },
        tessedit_pageseg_mode: "6",
      });

      const text = result.data.text.trim();
      const field = selectedFieldRef.current;
      const updated = { ...formRef.current, [field]: text };
      setForm(updated);
      formRef.current = updated;

      setOcrStatus("done");
      ocrStatusRef.current = "done";
      setOcrMode("done");

      setCrop(null);
      setCompletedCrop(null);
    } catch (err) {
      console.error("Tesseract error:", err);
      setOcrStatus("error");
      ocrStatusRef.current = "error";
      setOcrMode("error");
    } finally {
      setOcrLoading(false);
      ocrLoadingRef.current = false;
    }
  }, [completedCrop, pdfPageSize]);

  // ── Crop-based photo / signature upload ───────────────────────────────────
  const uploadCroppedImage = async (type) => {
    if (!completedCrop || !completedCrop.width || !completedCrop.height) {
      alert("पहले PDF पर कोई क्षेत्र खींचें (drag करके select करें)");
      return;
    }
    const folder = foldersRef.current[currentFolderIndexRef.current]?.name;
    if (!folder) {
      alert("Folder not found");
      return;
    }

    const pdfCanvas = pdfWrapperRef.current?.querySelector("canvas");
    if (!pdfCanvas) {
      alert("PDF canvas नहीं मिला। PDF लोड होने तक प्रतीक्षा करें।");
      return;
    }

    const cropped = cropPdfCanvas(
      pdfCanvas,
      completedCrop,
      pdfPageSize.width,
      pdfPageSize.height,
    );
    const base64 = cropped.toDataURL("image/jpeg", 0.95);
    setCropPreview({ base64, type });
    setPhotoSignUploading(type);

    try {
      const payload = {
        folderName: folder,
        photo: type === "photo" ? base64 : null,
        signature: type === "signature" ? base64 : null,
      };
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/upload-photo-signature`,
        payload,
        {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("workerToken")}`
            }}
      );
      if (res.data.success) {
        alert(
          `✅ ${type === "photo" ? "फोटो" : "हस्ताक्षर"} सफलतापूर्वक सहेजा गया!`,
        );
        setCrop(null);
        setCompletedCrop(null);
        setOcrMode("idle");
        setCropPreview(null);
      } else {
        alert("❌ Upload failed");
        setCropPreview(null);
      }
    } catch (err) {
      console.error(err);
      alert(
        "❌ Upload failed: " + (err.response?.data?.message || err.message),
      );
      setCropPreview(null);
    } finally {
      setPhotoSignUploading(false);
    }
  };

  const handleCropChange = (c) => {
    setCrop(c);
    if (ocrMode === "idle" || ocrMode === "done" || ocrMode === "error") {
      setOcrMode("selecting");
    }
  };

  const logout = () => {
    sessionStorage.clear("workerToken");
    sessionStorage.clear("phoneNumber");
    sessionStorage.clear("workerName");
    localStorage.clear();
    navigate("/");
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="container">
      {/* ── Error Popup ── */}
      {showErrorPopup && (
        <div className="error-popup-overlay">
          <div className="error-popup">
            <div className="error-popup-header">
              <h3>📝 त्रुटि रिपोर्ट करें</h3>
              <button
                className="close-btn"
                onClick={() => {
                  setShowErrorPopup(false);
                  setErrorMessage("");
                }}
              >
                ✕
              </button>
            </div>
            <div className="error-popup-content">
              <div className="error-details">
                <div className="detail-row">
                  <span className="detail-label">📱 फोन नंबर:</span>
                  <span className="detail-value">
                    {sessionStorage.getItem("phoneNumber") || "—"}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">👤 कार्यकर्ता नाम:</span>
                  <span className="detail-value">
                    {sessionStorage.getItem("workerName") || "Unknown"}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">📁 बंडल नाम:</span>
                  <span className="detail-value">
                    {foldersRef.current[currentFolderIndexRef.current]?.name ||
                      "—"}
                  </span>
                </div>
              </div>
              <div className="error-message-input">
                <label htmlFor="errorMessage">त्रुटि का विवरण:</label>
                <textarea
                  id="errorMessage"
                  rows="4"
                  placeholder="कृपया त्रुटि का विवरण दर्ज करें..."
                  value={errorMessage}
                  onChange={(e) => setErrorMessage(e.target.value)}
                  disabled={isSubmittingError}
                />
              </div>
            </div>
            <div className="error-popup-footer">
              <button
                className="cancel-btn"
                onClick={() => {
                  setShowErrorPopup(false);
                  setErrorMessage("");
                }}
                disabled={isSubmittingError}
              >
                रद्द करें
              </button>
              <button
                className="submit-error-btn"
                onClick={reportError}
                disabled={isSubmittingError || !errorMessage.trim()}
              >
                {isSubmittingError ? "⏳ भेज रहा है..." : "✅ रिपोर्ट करें"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Header ── */}
      <div className="header">
        <div className="header-icon">📋</div>
        <div>
          <h1 className="title">Resume</h1>
          <p className="subtitle">अभ्यर्थी की जानकारी स्कैन व भरें</p>
        </div>

        <a
          href="https://drive.google.com/file/d/1d2feyQ02rrfeZ3aFoSBPY5fkwZR7Xdng/edit"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "7px 14px",
            borderRadius: 6,
            background: "linear-gradient(135deg, #d97706, #b45309)",
            color: "#fff",
            fontWeight: 700,
            fontSize: 13,
            textDecoration: "none",
            boxShadow: "0 2px 6px rgba(217,119,6,0.45)",
            whiteSpace: "nowrap",
            transition: "all 0.2s ease",
          }}
          title="हिंदी कीबोर्ड डाउनलोड करें"
        >
          ⌨️ हिंदी Keyboard Download
        </a>

        <button
          className="nav-btn"
          onClick={() => navigate("/ProcessedResumeOCR")}
        >
          See the Processed data
        </button>
        <button className="nav-btn" onClick={logout}>
          Log out
        </button>
      </div>

      {/* ── Candidate Navigation ── */}
      <div className="candidate-bar">
        <button className="nav-btn" onClick={prevCandidate}>
          ◀ पिछला अभ्यर्थी
        </button>
        <div className="candidate-badge">
          <span className="candidate-label">अभ्यर्थी</span>
          <span className="candidate-name">
            {foldersRef.current[currentFolderIndexRef.current]?.name || "—"}
          </span>
          <span className="candidate-count">
            {currentFolderIndexRef.current + 1} /{" "}
            {foldersRef.current.length || 1}
          </span>
        </div>
        <button className="nav-btn" onClick={nextCandidate}>
          अगला अभ्यर्थी ▶
        </button>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          MAIN — flex container with draggable splitter
      ══════════════════════════════════════════════════════════════════ */}
      <div className="main" ref={mainContainerRef}>

        {/* ── PDF PANEL (left) ── */}
        <div
          className="image-panel"
          ref={leftPanelRef}
          style={{ width: `${leftWidthPct}%` }}
        >
          <div className="panel-header">
            <span className="panel-icon">📄</span>
            <h2>PDF दस्तावेज़</h2>
          </div>

          {/* Sticky Toolbar */}
          <div
            className="toolbar"
            style={{
              position: "sticky",
              top: 0,
              zIndex: 100,
              borderBottom: "1px solid rgba(255,255,255,0.08)",
              flexWrap: "wrap",
              gap: 6,
            }}
          >
            {/* Page navigation group */}
            <div className="toolbar-group">
              <button
                className="toolbar-btn"
                disabled={pageNumber <= 1}
                onClick={() => setPageNumber((p) => p - 1)}
                title="Previous Page"
              >
                <span className="btn-icon">&#9664;</span>
                <span className="btn-label">Prev</span>
              </button>
              <span className="page-info">
                <span className="page-current">{pageNumber}</span>
                <span className="page-sep">/</span>
                <span className="page-total">{numPages ?? "—"}</span>
              </span>
              <button
                className="toolbar-btn"
                disabled={!numPages || pageNumber >= numPages}
                onClick={() => setPageNumber((p) => p + 1)}
                title="Next Page"
              >
                <span className="btn-label">Next</span>
                <span className="btn-icon">&#9654;</span>
              </button>
            </div>

            <div className="toolbar-divider" />

            {/* Zoom group */}
            <div className="toolbar-group">
              <button
                className="toolbar-btn icon-only"
                onClick={zoomOut}
                title="Zoom Out"
              >
                &#8722;
              </button>
              <span className="zoom-label">{Math.round(pdfScale * 100)}%</span>
              <button
                className="toolbar-btn icon-only"
                onClick={zoomIn}
                title="Zoom In"
              >
                &#43;
              </button>
            </div>

            <div className="toolbar-divider" />

            {/* Fit group */}
            <div className="toolbar-group">
              <button
                className="toolbar-btn"
                onClick={fitWidth}
                title="Fit Width"
              >
                &#8596; Width
              </button>
              <button
                className="toolbar-btn"
                onClick={fitHeight}
                title="Fit Height"
              >
                &#8597; Height
              </button>
              <button
                className="toolbar-btn"
                onClick={calcFit}
                title="Fit Page"
              >
                &#9974; Fit
              </button>
            </div>

            <div className="toolbar-divider" />

            {/* ✅ NEW: Rotate group */}
            <div className="toolbar-group">
              <button
                className="toolbar-btn icon-only"
                onClick={rotateLeft}
                title="Rotate Left (↺ −90°)"
                style={{ fontSize: 16 }}
              >
                ↺
              </button>

              {/* Rotation angle badge — only shown when non-zero */}
              {rotation !== 0 && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#57c8ff",
                    background: "rgba(87,200,255,0.12)",
                    border: "1px solid rgba(87,200,255,0.3)",
                    borderRadius: 4,
                    padding: "1px 6px",
                    minWidth: 32,
                    textAlign: "center",
                    lineHeight: "20px",
                    userSelect: "none",
                  }}
                  title={`Current rotation: ${rotation}°`}
                >
                  {rotation}°
                </span>
              )}

              <button
                className="toolbar-btn icon-only"
                onClick={rotateRight}
                title="Rotate Right (↻ +90°)"
                style={{ fontSize: 16 }}
              >
                ↻
              </button>
            </div>

            <div className="toolbar-divider" />

            {/* Action buttons */}
            <div className="toolbar-group" style={{ marginLeft: "auto" }}>
              <button
                onClick={runOCR}
                disabled={ocrMode === "processing"}
                title="PDF से Scan करें (पहले field चुनें, फिर PDF पर खींचें)"
                style={{
                  padding: "6px 14px",
                  borderRadius: 6,
                  border: "none",
                  cursor: ocrMode === "processing" ? "not-allowed" : "pointer",
                  fontWeight: 700,
                  fontSize: 12,
                  color: "#fff",
                  background:
                    ocrMode === "processing"
                      ? "#374151"
                      : "linear-gradient(135deg, #1d4ed8, #1e40af)",
                  boxShadow:
                    ocrMode === "processing"
                      ? "none"
                      : "0 2px 6px rgba(29,78,216,0.45)",
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  whiteSpace: "nowrap",
                  transition: "all 0.2s ease",
                  opacity: ocrMode === "processing" ? 0.7 : 1,
                }}
              >
                {ocrMode === "processing" ? (
                  <>
                    <span
                      style={{
                        display: "inline-block",
                        width: 11,
                        height: 11,
                        border: "2px solid rgba(255,255,255,0.4)",
                        borderTopColor: "#fff",
                        borderRadius: "50%",
                        animation: "ocr-spin 0.7s linear infinite",
                        flexShrink: 0,
                      }}
                    />
                    {ocrProgress}%
                  </>
                ) : (
                  <>🔍 Scan करें</>
                )}
              </button>

              <button
                onClick={() => setShowErrorPopup(true)}
                title="इस अभ्यर्थी के लिए त्रुटि रिपोर्ट करें"
                style={{
                  padding: "6px 14px",
                  borderRadius: 6,
                  border: "none",
                  cursor: "pointer",
                  fontWeight: 700,
                  fontSize: 12,
                  color: "#fff",
                  background: "linear-gradient(135deg, #dc2626, #b91c1c)",
                  boxShadow: "0 2px 6px rgba(220,38,38,0.45)",
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  whiteSpace: "nowrap",
                  transition: "all 0.2s ease",
                }}
              >
                ⚠️ Error
              </button>
            </div>
          </div>

          {/* OCR status bar */}
          <div
            className={`ocr-status-bar ${ocrStatus}`}
            style={{ margin: "0", borderRadius: 0 }}
          >
            {ocrMode === "idle" && !selectedFieldRef.current && (
              <span className="status-hint">
                👆 पहले कोई field पर click करें
              </span>
            )}
            {ocrMode === "idle" && selectedFieldRef.current && (
              <span className="status-hint active">
                ✏️ फ़ील्ड चुना: <strong>{selectedFieldRef.current}</strong> — अब
                PDF पर क्षेत्र खींचें
              </span>
            )}
            {ocrMode === "selecting" && (
              <span className="status-hint active">
                ✂️ क्षेत्र चुना जा रहा है — &quot;Scan करें&quot; दबाएं
              </span>
            )}
            {ocrMode === "processing" && (
              <span className="status-loading">
                ⏳ OCR चल रहा है ({ocrProgress}%) —{" "}
                <strong>{selectedFieldRef.current}</strong>
              </span>
            )}
            {ocrMode === "done" && (
              <span className="status-done">
                ✅ Text भर दिया गया: <strong>{selectedFieldRef.current}</strong>
              </span>
            )}
            {ocrMode === "error" && (
              <span className="status-error">
                ❌ OCR में त्रुटि हुई, दोबारा कोशिश करें
              </span>
            )}
          </div>

          {/* PDF area with ReactCrop overlay */}
          <div className="pdf-area" ref={pdfContainerRef}>
            {currentPdfUrl ? (
              <div
                style={{ position: "relative", display: "inline-block" }}
                ref={pdfWrapperRef}
              >
                <Document
                  file={currentPdfUrl}
                  onLoadSuccess={onPdfLoadSuccess}
                  loading={<div className="pdf-loading">Loading PDF…</div>}
                  error={<div className="pdf-error">Failed to load PDF.</div>}
                >
                  {/*
                    ✅ Pass `rotate` prop to react-pdf <Page>.
                    react-pdf accepts 0 | 90 | 180 | 270.
                    Our state cycles through exactly those values via modulo 360.
                  */}
                  <Page
                    pageNumber={pageNumber}
                    scale={pdfScale}
                    rotate={rotation}
                    renderAnnotationLayer={false}
                    renderTextLayer={true}
                    onRenderSuccess={onPdfPageRenderSuccess}
                  />
                </Document>

                {pdfPageSize.width > 0 && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: pdfPageSize.width,
                      height: pdfPageSize.height,
                      zIndex: 10,
                    }}
                  >
                    <ReactCrop
                      crop={crop}
                      onChange={handleCropChange}
                      onComplete={(c) => setCompletedCrop(c)}
                      style={{
                        display: "block",
                        width: "100%",
                        height: "100%",
                      }}
                    >
                      <img
                        src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
                        alt=""
                        style={{
                          width: pdfPageSize.width,
                          height: pdfPageSize.height,
                          opacity: 0,
                          display: "block",
                          userSelect: "none",
                          pointerEvents: "none",
                        }}
                        draggable={false}
                      />
                    </ReactCrop>
                  </div>
                )}

                {ocrMode === "processing" && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background: "#222",
                      zIndex: 20,
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${ocrProgress}%`,
                        background: "#57c8ff",
                        transition: "width .3s ease",
                      }}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="pdf-loading">
                <span>📄</span>
                <p>कोई PDF नहीं</p>
              </div>
            )}
          </div>

          {/* Language info note */}
          <div className="ocr-section" style={{ padding: "8px 12px" }}>
            <div
              style={{
                fontSize: 11,
                color: "#9ca3af",
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
              }}
            >
              <span
                style={{
                  display: "inline-block",
                  padding: "2px 8px",
                  background: "rgba(29,78,216,0.15)",
                  border: "1px solid rgba(29,78,216,0.3)",
                  borderRadius: 4,
                  color: "#93c5fd",
                  fontWeight: 600,
                  fontSize: 10,
                  letterSpacing: "0.05em",
                }}
              >
                eng+hin
              </span>
              Hindi &amp; English OCR सक्रिय है
            </div>
          </div>
        </div>

        {/* ── SPLITTER ── */}
        <div
          className="resizer"
          onMouseDown={onSplitterMouseDown}
          title="Drag to resize panels"
        >
          <div className="resizer-handle" />
          <div className="resizer-dots">
            <span /><span /><span /><span /><span /><span />
          </div>
        </div>

        {/* ── FORM PANEL (right) ── */}
        <div className="form-panel">
          <div className="panel-header">
            <span className="panel-icon">📝</span>
            <h2>अभ्यर्थी विवरण</h2>
          </div>

          {/* Section 1 — Basic Info */}
          <div className="form-section">
            <div className="section-title">
              <span className="section-num">1</span>मूल जानकारी
            </div>

            <div className="field-group">
              <label>पद का नाम</label>
              <select
                className={fieldClass("post")}
                value={formRef.current.post}
                onFocus={() => setSelectedField("post")}
                onChange={(e) => {
                  const u = { ...formRef.current, post: e.target.value };
                  setForm(u);
                  formRef.current = u;
                }}
              >
                <option value="">पद का नाम चुनें</option>

                {postOptions.map((p, i) => (
                  <option key={i} value={p.hindi}>
                    {p.hindi} ({p.english})
                  </option>
                ))}
              </select>

                {/* vibhad */}
                <div className="field-group">
                <label>विभाग</label>
                <select
                  className={fieldClass("vibhag")}
                  value={formRef.current.vibhag}
                  onFocus={() => setSelectedField("vibhag")}
                  onChange={(e) => {
                    const u = { ...formRef.current, vibhag: e.target.value };
                    setForm(u);
                    formRef.current = u;
                  }}
                >
                  <option value="">विभाग चुनें</option>

                  {vibhagOptions.map((v, i) => (
                    <option key={i} value={v.hindi}>
                      {v.hindi} ({v.english})
                    </option>
                  ))}
                </select>
              </div>

            </div>

            <div className="two-col">
              <div className="field-group">
                <label>नाम (English)</label>
                <input
                  className={fieldClass("nameEnglish")}
                  placeholder="Full Name in English"
                  value={formRef.current.nameEnglish}
                  onFocus={() => setSelectedField("nameEnglish")}
                  onChange={(e) => {
                    const u = {
                      ...formRef.current,
                      nameEnglish: e.target.value,
                    };
                    setForm(u);
                    formRef.current = u;
                  }}
                />
              </div>
              <div className="field-group">
                <label>नाम (हिंदी)</label>
                <input
                  className={fieldClass("nameHindi")}
                  placeholder="पूरा नाम हिंदी में"
                  value={formRef.current.nameHindi}
                  onFocus={() => setSelectedField("nameHindi")}
                  onChange={(e) => {
                    const u = { ...formRef.current, nameHindi: e.target.value };
                    setForm(u);
                    formRef.current = u;
                  }}
                />
              </div>
            </div>

            <div className="two-col">
              <div className="field-group">
                <label>पिता / पति का नाम</label>
                <input
                  className={fieldClass("fatherName")}
                  placeholder="पिता / पति का नाम"
                  value={formRef.current.fatherName}
                  onFocus={() => setSelectedField("fatherName")}
                  onChange={(e) => {
                    const u = {
                      ...formRef.current,
                      fatherName: e.target.value,
                    };
                    setForm(u);
                    formRef.current = u;
                  }}
                />
              </div>
              <div className="field-group">
                <label>जन्म तिथि</label>
                <input
                  type="date"
                  className={fieldClass("dob")}
                  value={formRef.current.dob}
                  onFocus={() => setSelectedField("dob")}
                  onChange={(e) => {
                    const u = { ...formRef.current, dob: e.target.value };
                    setForm(u);
                    formRef.current = u;
                  }}
                />
              </div>
            </div>

            <div className="field-group">
              <label>लिंग</label>
              <div className="radio-group">
                {[
                  ["male", "पुरुष"],
                  ["female", "महिला"],
                  ["other", "अन्य"],
                ].map(([val, lbl]) => (
                  <label
                    key={val}
                    className={`radio-option ${formRef.current.gender === val ? "radio-active" : ""}`}
                  >
                    <input
                      type="radio"
                      name="gender"
                      value={val}
                      checked={formRef.current.gender === val}
                      onChange={(e) => {
                        const u = {
                          ...formRef.current,
                          gender: e.target.value,
                        };
                        setForm(u);
                        formRef.current = u;
                      }}
                    />
                    {lbl}
                  </label>
                ))}
              </div>
            </div>

            <div className="two-col">
              <div className="field-group">
                <label>श्रेणी</label>
                <select
                  className={fieldClass("category")}
                  value={formRef.current.category}
                  onFocus={() => setSelectedField("category")}
                  onChange={(e) => {
                    const u = { ...formRef.current, category: e.target.value };
                    setForm(u);
                    formRef.current = u;
                  }}
                >
                  <option value="">श्रेणी चुनें</option>
                  <option>General</option>
                  <option>EWS</option>
                  <option>OBC</option>
                  <option>SC</option>
                  <option>ST</option>
                </select>
              </div>
              <div className="field-group">
                <label>धर्म</label>
                <input
                  list="religion-options"
                  className={fieldClass("religion")}
                  placeholder="धर्म"
                  value={formRef.current.religion}
                  onFocus={() => setSelectedField("religion")}
                  onChange={(e) => {
                    const u = { ...formRef.current, religion: e.target.value };
                    setForm(u);
                    formRef.current = u;
                  }}
                />
                <datalist id="religion-options">
                  <option value="Hindu" />
                  <option value="Muslim" />
                  <option value="Sikh" />
                  <option value="Christian" />
                  <option value="Jain" />
                  <option value="Buddhist" />
                  <option value="Other" />
                </datalist>
              </div>
            </div>

            <div className="two-col">
              <div className="field-group">
                <label>जाति</label>
                <input
                  className={fieldClass("caste")}
                  placeholder="जाति"
                  value={formRef.current.caste}
                  onFocus={() => setSelectedField("caste")}
                  onChange={(e) => {
                    const u = { ...formRef.current, caste: e.target.value };
                    setForm(u);
                    formRef.current = u;
                  }}
                />
              </div>
              <div className="field-group">
                <label>मोबाइल नंबर</label>
                <div>
                  <input
                    className={`${fieldClass("mobile")} ${validationErrors.mobile ? "error-field" : ""}`}
                    placeholder="मोबाइल नंबर (10 अंक)"
                    value={formRef.current.mobile}
                    onFocus={() => setSelectedField("mobile")}
                    onChange={(e) => handleMobileChange(e.target.value)}
                    maxLength={10}
                  />
                  {validationErrors.mobile && (
                    <small
                      style={{
                        color: "#ef4444",
                        marginTop: 4,
                        display: "block",
                      }}
                    >
                      ⚠️ {validationErrors.mobile}
                    </small>
                  )}
                </div>
              </div>
            </div>

            <div className="field-group">
              <label>ई-मेल आईडी</label>
              <div>
                <input
                  className={`${fieldClass("email")} ${validationErrors.email ? "error-field" : ""}`}
                  placeholder="ई-मेल आईडी (जैसे: name@example.com)"
                  value={formRef.current.email}
                  onFocus={() => setSelectedField("email")}
                  onChange={(e) => handleEmailChange(e.target.value)}
                />
                {validationErrors.email && (
                  <small
                    style={{ color: "#ef4444", marginTop: 4, display: "block" }}
                  >
                    ⚠️ {validationErrors.email}
                  </small>
                )}
              </div>
            </div>

            <div className="field-group">
              <label>वर्तमान पता</label>
              <textarea
                className={`${fieldClass("address")} field-textarea`}
                placeholder="वर्तमान पता"
                value={formRef.current.address}
                onFocus={() => setSelectedField("address")}
                onChange={(e) => {
                  const u = { ...formRef.current, address: e.target.value };
                  setForm(u);
                  formRef.current = u;
                }}
              />
              <div className="field-group">
                <label
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <input
                    type="checkbox"
                    checked={formRef.current.sameAddress || false}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      const u = {
                        ...formRef.current,
                        sameAddress: isChecked,
                        correspondenceAddress: isChecked
                          ? formRef.current.address
                          : "",
                      };
                      setForm(u);
                      formRef.current = u;
                    }}
                  />
                  वर्तमान पता ही पत्राचार पता है
                </label>
              </div>

              <div className="field-group">
                <label>पत्राचार पता</label>
                <textarea
                  className={`${fieldClass("correspondenceAddress")} field-textarea`}
                  placeholder="पत्राचार पता"
                  value={formRef.current.correspondenceAddress}
                  onFocus={() => setSelectedField("correspondenceAddress")}
                  onChange={(e) => {
                    const u = {
                      ...formRef.current,
                      correspondenceAddress: e.target.value,
                    };
                    setForm(u);
                    formRef.current = u;
                  }}
                  disabled={formRef.current.sameAddress}
                />
              </div>
            </div>

            <div className="field-group">
              <label>पिन कोड</label>
              <div>
                <input
                  className={`${fieldClass("pinCode")} ${validationErrors.pinCode ? "error-field" : ""}`}
                  placeholder="पिन कोड (6 अंक)"
                  value={formRef.current.pinCode}
                  onFocus={() => setSelectedField("pinCode")}
                  onChange={(e) => handlePinCodeChange(e.target.value)}
                  maxLength={6}
                />
                {validationErrors.pinCode && (
                  <small
                    style={{ color: "#ef4444", marginTop: 4, display: "block" }}
                  >
                    ⚠️ {validationErrors.pinCode}
                  </small>
                )}
              </div>
            </div>
          </div>

          {/* Section 2 — Educational Qualification */}
          <div className="form-section">
            <div className="section-title">
              <span className="section-num">2</span>शैक्षिक योग्यता
            </div>

            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>क्र.सं.</th>
                    <th>परीक्षा का नाम</th>
                    <th>बोर्ड / विश्वविद्यालय</th>
                    <th>Score Type</th>
                    <th>पूर्णांक</th>
                    <th>प्राप्तांक</th>
                    <th>Result</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {eduRows.map((row, index) => (
                    <tr key={row.id}>
                      <td>{index + 1}</td>
                      <td>
                        <select
                          className="table-input"
                          value={row.exam}
                          onChange={(e) =>
                            updateEduRow(row.id, "exam", e.target.value)
                          }
                        >
                          <option value="">Select</option>
                          <option value="Highschool">Highschool</option>
                          <option value="Intermediate">Intermediate</option>
                          <option value="Graduation">Graduation</option>
                          <option value="Post Graduation">Post Graduation</option>
                          <option value="CCC">CCC</option>
                          <option value="O-Level">O-Level</option>
                          <option value="Other">Other</option>
                        </select>
                        {row.exam === "Other" && (
                          <input
                            className="table-input"
                            placeholder="Custom Exam"
                            value={row.customExam}
                            onChange={(e) =>
                              updateEduRow(row.id, "customExam", e.target.value)
                            }
                          />
                        )}
                      </td>
                      <td>
                        <input
                            list={`board-options-${row.id}`}
                            className="table-input"
                            value={row.board}
                            placeholder="Board / University"
                            onChange={(e) =>
                              updateEduRow(row.id, "board", e.target.value)
                            }
                          />

                          <datalist id={`board-options-${row.id}`}>
                            <option value="UP BOARD" />
                            <option value="CBSE BOARD" />
                            <option value="DRBAU" />
                            <option value="AKTU" />
                            <option value="NIELIT" />
                          </datalist>
                      </td>
                      <td>
                        <select
                          className="table-input"
                          value={row.scoreType}
                          onChange={(e) =>
                            updateEduRow(row.id, "scoreType", e.target.value)
                          }
                        >
                          <option value="Percentage">Percentage</option>
                          <option value="CGPA">CGPA</option>
                          <option value="Grade">Grade</option>
                        </select>
                      </td>
                      <td>
                        <input
                          className="table-input small"
                          value={row.totalMarks}
                          placeholder="Total"
                          onChange={(e) =>
                            updateEduRow(row.id, "totalMarks", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="table-input small"
                          value={row.obtainedMarks}
                          placeholder={
                            row.scoreType === "CGPA"
                              ? "CGPA"
                              : row.scoreType === "Grade"
                                ? "Grade"
                                : "Marks"
                          }
                          onChange={(e) =>
                            updateEduRow(row.id, "obtainedMarks", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="table-input small"
                          value={row.percentage}
                          readOnly
                          placeholder={
                            row.scoreType === "CGPA"
                              ? "CGPA"
                              : row.scoreType === "Grade"
                                ? "Grade"
                                : "%"
                          }
                        />
                      </td>
                      <td>
                        <button onClick={() => deleteEduRow(row.id)}>🗑</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button className="add-row-btn" onClick={addEduRow}>
              + Add Qualification
            </button>
          </div>

          {/* Section 3 — Typing & Shorthand */}
          <div className="form-section">
            <div className="section-title">
              <span className="section-num">3</span>टंकण एवं आशुलिपि की दक्षता
            </div>
            <div className="two-col">
              <div className="field-group">
                <label>हिंदी टंकण गति</label>
                <input
                  className={fieldClass("hindiTyping")}
                  placeholder="जैसे: 25 w/m"
                  value={formRef.current.hindiTyping}
                  onFocus={() => setSelectedField("hindiTyping")}
                  onChange={(e) => {
                    const u = {
                      ...formRef.current,
                      hindiTyping: e.target.value,
                    };
                    setForm(u);
                    formRef.current = u;
                  }}
                />
              </div>
              <div className="field-group">
                <label>अंग्रेजी टंकण गति</label>
                <input
                  className={fieldClass("englishTyping")}
                  placeholder="जैसे: 40 w/m"
                  value={formRef.current.englishTyping}
                  onFocus={() => setSelectedField("englishTyping")}
                  onChange={(e) => {
                    const u = {
                      ...formRef.current,
                      englishTyping: e.target.value,
                    };
                    setForm(u);
                    formRef.current = u;
                  }}
                />
              </div>
            </div>
            <div className="field-group">
              <label>आशुलिपि गति</label>
              <input
                className={fieldClass("shorthandSpeed")}
                placeholder="जैसे: 80 w/m"
                value={formRef.current.shorthandSpeed}
                onFocus={() => setSelectedField("shorthandSpeed")}
                onChange={(e) => {
                  const u = {
                    ...formRef.current,
                    shorthandSpeed: e.target.value,
                  };
                  setForm(u);
                  formRef.current = u;
                }}
              />
            </div>
          </div>

          {/* Section 4 — Aadhar Number */}
          <div className="form-section">
            <div className="section-title">
              <span className="section-num">4</span>आधार संख्या
            </div>
            <div className="field-group">
              <label>आधार संख्या</label>
              <div>
                <input
                  className={`${fieldClass("aadharNumber")} ${validationErrors.aadhar ? "error-field" : ""}`}
                  placeholder="आधार संख्या (12 अंक)"
                  value={formRef.current.aadharNumber}
                  onFocus={() => setSelectedField("aadharNumber")}
                  onChange={(e) => handleAadharChange(e.target.value)}
                  maxLength={12}
                />
                {validationErrors.aadhar && (
                  <small
                    style={{ color: "#ef4444", marginTop: 4, display: "block" }}
                  >
                    ⚠️ {validationErrors.aadhar}
                  </small>
                )}
              </div>
            </div>
          </div>

          {/* Section 5 — Payment */}
          <div className="form-section">
            <div className="section-title">
              <span className="section-num">5</span>भुगतान जानकारी
            </div>
            <div className="two-col">
              <div className="field-group">
                <label>भुगतान विधि</label>
                <select
                  className={fieldClass("paymentMethod")}
                  value={formRef.current.paymentMethod}
                  onFocus={() => setSelectedField("paymentMethod")}
                  onChange={(e) => {
                    const u = {
                      ...formRef.current,
                      paymentMethod: e.target.value,
                      paymentNumber: "",
                    };
                    setForm(u);
                    formRef.current = u;
                  }}
                >
                  <option value="">भुगतान विधि चुनें</option>
                  {paymentOptions.map((m, i) => (
                    <option key={i} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field-group">
                <label>
                  {formRef.current.paymentMethod === "Check"
                    ? "चेक नंबर"
                    : formRef.current.paymentMethod === "UPI"
                      ? "UPI आईडी"
                      : formRef.current.paymentMethod === "Bank"
                        ? "बैंक खाता संख्या"
                        : formRef.current.paymentMethod === "NEFT"
                          ? "NEFT लेन-देन संख्या"
                          : formRef.current.paymentMethod === "IMPS"
                            ? "IMPS लेन-देन संख्या"
                            : formRef.current.paymentMethod === "RTGS"
                              ? "RTGS लेन-देन संख्या"
                              : "भुगतान संख्या"}
                </label>
                <input
                  className={fieldClass("paymentNumber")}
                  type="text"
                  placeholder={getPaymentNumberPlaceholder()}
                  value={formRef.current.paymentNumber}
                  onFocus={() => setSelectedField("paymentNumber")}
                  disabled={!formRef.current.paymentMethod}
                  onChange={(e) => {
                    const u = {
                      ...formRef.current,
                      paymentNumber: e.target.value,
                    };
                    setForm(u);
                    formRef.current = u;
                  }}
                />
                {!formRef.current.paymentMethod && (
                  <small
                    style={{ color: "#666", marginTop: 4, display: "block" }}
                  >
                    पहले भुगतान विधि चुनें
                  </small>
                )}
              </div>
            </div>
          </div>

          {/* Section 6 — Work Experience */}
          <div className="form-section">
            <div className="section-title">
              <span className="section-num">6</span>कार्य अनुभव का विवरण
            </div>
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>क्र.सं.</th>
                    <th>संस्था का नाम एवं पता</th>
                    <th>अवधि (कब से कब तक)</th>
                    <th>पद</th>
                    <th>वेतनमान</th>
                  </tr>
                </thead>
                <tbody>
                  {workRows.map((row) => (
                    <tr key={row.id}>
                      <td className="row-num">
                        {String(row.id).padStart(2, "0")}
                      </td>
                      <td>
                        <textarea
                          className="table-input table-textarea"
                          value={row.orgName}
                          placeholder="संस्था का नाम एवं पता"
                          onChange={(e) =>
                            updateWorkRow(row.id, "orgName", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="table-input"
                          value={row.duration}
                          placeholder="01/2020 से 03/2023"
                          onChange={(e) =>
                            updateWorkRow(row.id, "duration", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="table-input"
                          value={row.post}
                          placeholder="पद"
                          onChange={(e) =>
                            updateWorkRow(row.id, "post", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="table-input"
                          value={row.salary}
                          placeholder="वेतन"
                          onChange={(e) =>
                            updateWorkRow(row.id, "salary", e.target.value)
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button className="add-row-btn" onClick={addWorkRow}>
              + पंक्ति जोड़ें
            </button>
          </div>

          {/* Section 7 — Photo & Signature */}
          <div className="form-section">
            <div className="section-title">
              <span className="section-num">7</span>फोटो एवं हस्ताक्षर (PDF से)
            </div>

            <div
              style={{
                fontSize: 13,
                color: "#4a5568",
                marginBottom: 14,
                background: "linear-gradient(135deg, #ebf4ff, #e8f0fe)",
                padding: "10px 14px",
                borderRadius: 8,
                border: "1px solid #c3d9f7",
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
              }}
            >
              <span style={{ fontSize: 16, flexShrink: 0 }}>📌</span>
              <div>
                <strong>निर्देश:</strong> PDF पर माउस से क्षेत्र खींचें (drag
                करें), फिर नीचे
                <strong> "Save Photo"</strong> या{" "}
                <strong>"Save Signature"</strong> बटन दबाएं।
              </div>
            </div>

            {cropPreview && (
              <div
                style={{
                  marginBottom: 16,
                  padding: "12px 14px",
                  background: "#f0fdf4",
                  border: "1px solid #86efac",
                  borderRadius: 10,
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                }}
              >
                <img
                  src={cropPreview.base64}
                  alt="crop preview"
                  style={{
                    height: 80,
                    maxWidth: 200,
                    objectFit: "contain",
                    border: "2px solid #4ade80",
                    borderRadius: 6,
                    background: "#fff",
                  }}
                />
                <div>
                  <div
                    style={{
                      fontWeight: 700,
                      color: "#15803d",
                      fontSize: 14,
                      marginBottom: 4,
                    }}
                  >
                    {cropPreview.type === "photo"
                      ? "📸 फोटो Preview"
                      : "✍️ हस्ताक्षर Preview"}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "#166534",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        width: 12,
                        height: 12,
                        border: "2px solid #16a34a",
                        borderTopColor: "transparent",
                        borderRadius: "50%",
                        animation: "ocr-spin 0.7s linear infinite",
                      }}
                    />
                    Upload हो रहा है…
                  </div>
                </div>
              </div>
            )}

            <div className="two-col">
              <button
                onClick={() => uploadCroppedImage("photo")}
                disabled={!!photoSignUploading}
                style={{
                  padding: "11px 16px",
                  borderRadius: 8,
                  border: "none",
                  cursor: photoSignUploading ? "not-allowed" : "pointer",
                  fontWeight: 700,
                  fontSize: 14,
                  color: "#fff",
                  background:
                    photoSignUploading === "photo"
                      ? "#9ca3af"
                      : "linear-gradient(135deg, #1d4ed8, #1e40af)",
                  boxShadow:
                    photoSignUploading === "photo"
                      ? "none"
                      : "0 2px 8px rgba(29,78,216,0.35)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  transition: "all 0.2s ease",
                  width: "100%",
                }}
              >
                {photoSignUploading === "photo" ? (
                  <>
                    <span
                      style={{
                        display: "inline-block",
                        width: 14,
                        height: 14,
                        border: "2px solid rgba(255,255,255,0.5)",
                        borderTopColor: "#fff",
                        borderRadius: "50%",
                        animation: "ocr-spin 0.7s linear infinite",
                        flexShrink: 0,
                      }}
                    />
                    सहेज रहा है…
                  </>
                ) : (
                  <>📸 Save Photo (PDF से)</>
                )}
              </button>

              <button
                onClick={() => uploadCroppedImage("signature")}
                disabled={!!photoSignUploading}
                style={{
                  padding: "11px 16px",
                  borderRadius: 8,
                  border: "none",
                  cursor: photoSignUploading ? "not-allowed" : "pointer",
                  fontWeight: 700,
                  fontSize: 14,
                  color: "#fff",
                  background:
                    photoSignUploading === "signature"
                      ? "#9ca3af"
                      : "linear-gradient(135deg, #7c3aed, #6d28d9)",
                  boxShadow:
                    photoSignUploading === "signature"
                      ? "none"
                      : "0 2px 8px rgba(124,58,237,0.35)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  transition: "all 0.2s ease",
                  width: "100%",
                }}
              >
                {photoSignUploading === "signature" ? (
                  <>
                    <span
                      style={{
                        display: "inline-block",
                        width: 14,
                        height: 14,
                        border: "2px solid rgba(255,255,255,0.5)",
                        borderTopColor: "#fff",
                        borderRadius: "50%",
                        animation: "ocr-spin 0.7s linear infinite",
                        flexShrink: 0,
                      }}
                    />
                    सहेज रहा है…
                  </>
                ) : (
                  <>✍️ Save Signature (PDF से)</>
                )}
              </button>
            </div>

            <p
              style={{
                fontSize: 11,
                color: "#9ca3af",
                marginTop: 10,
                textAlign: "center",
              }}
            >
              ✅ Save होने के बाद crop अपने आप reset हो जाएगा
            </p>
          </div>

          <button className="save-btn" onClick={saveData}>
            💾 सुरक्षित करें
          </button>
        </div>
        {/* end form-panel */}

      </div>
      {/* end .main */}
    </div>
  );
}

export default ResumeOCR;
